import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Ticket } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import ConfigurableTicketTable from './ConfigurableTicketTable';
import EnhancedUrgencyIndicator from './EnhancedUrgencyIndicator';
import { isTicketClosed, isTicketOpen, getStatusDisplayName, getStatusColorClasses } from '../../utils/statusUtils';
import BoardToolbar from './BoardToolbar';
import { useDebouncedCallback } from '../../hooks/useDebounced';

interface TicketListProps {
  tickets: Ticket[];
  loading: boolean;
  onTicketSelect: (ticket: Ticket) => void;
  onRefresh: () => void;
  onCreateTicket?: () => void;
  currentView?: string;
  lastUpdated?: Date | null;
  autoUpdating?: boolean;
  onCustomerMessage?: (ticketId: string) => void;
  onAgentReply?: (ticketId: string) => void;
  pagination?: any;
  currentPage?: number;
  itemsPerPage?: number;
  totalItems?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  savedFilters?: {
    statusFilter: string;
    priorityFilter: string;
    searchTerm: string;
    sortBy: string;
    sortOrder: string;
  };
  onFiltersChange?: (filters: {
    statusFilter?: string;
    priorityFilter?: string;
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => void;
}

const TicketList: React.FC<TicketListProps> = ({
  tickets,
  loading,
  onTicketSelect,
  onRefresh,
  onCreateTicket,
  currentView = 'tickets',
  lastUpdated,
  autoUpdating = false,
  onCustomerMessage,
  onAgentReply,
  pagination,
  currentPage = 1,
  itemsPerPage = 50,
  totalItems = 0,
  totalPages = 0,
  onPageChange,
  onItemsPerPageChange,
  savedFilters,
  onFiltersChange
}) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    search: savedFilters?.searchTerm || '',
    agent: ['all'] as string[],
    customerType: ['all'] as string[],
    category: ['all'] as string[],
    dateRange: ['all'] as string[],
    priority: savedFilters?.priorityFilter ? [savedFilters.priorityFilter] : ['all'] as string[],
    status: savedFilters?.statusFilter ? [savedFilters.statusFilter] : ['all'] as string[]
  });
  const [deletingTicketId, setDeletingTicketId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [blinkingRows, setBlinkingRows] = useState<Map<string, { urgency: string; startTime: number }>>(new Map());
  const [messageBlinkingRows, setMessageBlinkingRows] = useState<Set<string>>(new Set());
  const [agents, setAgents] = useState<any[]>([]);
  const [agentPresence, setAgentPresence] = useState<Map<string, boolean>>(new Map());
  const [reassignTicketId, setReassignTicketId] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState(false);

  // Update current time every 10 seconds to refresh relative times and "last updated" display
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCurrentTime(now);
      
      // Clean up expired blinking rows (after 10 seconds)
      setBlinkingRows(prev => {
        const updated = new Map(prev);
        Array.from(updated.entries()).forEach(([ticketId, data]) => {
          if (now - data.startTime > 10000) { // 10 seconds
            updated.delete(ticketId);
          }
        });
        return updated.size !== prev.size ? updated : prev;
      });
    }, 1000); // Update every second for more responsive blinking cleanup

    return () => clearInterval(interval);
  }, []);

  // Update local state when savedFilters prop changes
  useEffect(() => {
    if (savedFilters) {
      setFilters(prev => ({
        ...prev,
        search: savedFilters.searchTerm || '',
        status: savedFilters.statusFilter ? [savedFilters.statusFilter] : ['all'],
        priority: savedFilters.priorityFilter ? [savedFilters.priorityFilter] : ['all']
      }));
    }
  }, [savedFilters]);

  // Debounced filter change for preferences saving
  const debouncedFiltersChange = useDebouncedCallback(
    (newFilters: any) => {
      if (onFiltersChange) {
        // Convert array filters back to single values for backward compatibility
        const legacyFilters: any = {};
        if (newFilters.search !== undefined) legacyFilters.searchTerm = newFilters.search;
        if (newFilters.status) {
          const statusArr = Array.isArray(newFilters.status) ? newFilters.status : [newFilters.status];
          legacyFilters.statusFilter = statusArr.includes('all') || statusArr.length === 0 ? 'all' : statusArr[0];
        }
        if (newFilters.priority) {
          const priorityArr = Array.isArray(newFilters.priority) ? newFilters.priority : [newFilters.priority];
          legacyFilters.priorityFilter = priorityArr.includes('all') || priorityArr.length === 0 ? 'all' : priorityArr[0];
        }
        onFiltersChange(legacyFilters);
      }
    },
    500, // Debounce preference saving
    [onFiltersChange]
  );

  // Handle filters change from BoardToolbar
  const handleFiltersChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    debouncedFiltersChange(newFilters);
  };

  // Load agents for reassign functionality
  useEffect(() => {
    const loadAgents = async () => {
      if (user?.role === 'agent' || user?.userType === 'agent') {
        try {
          const fetchedAgents = await apiService.getAgents();
          setAgents(fetchedAgents);
        } catch (error) {
          console.error('Error loading agents:', error);
        }
      }
    };

    loadAgents();
    
    // Listen for agent status changes
    const handleAgentStatusChange = (data: { agentId: string; isOnline: boolean; lastSeen: string }) => {
      console.log('👨‍💼 TicketList: Agent status changed:', data);
      setAgentPresence(prev => {
        const updated = new Map(prev);
        updated.set(data.agentId, data.isOnline);
        return updated;
      });
      
      // Also update the agents list with real-time status
      setAgents(prev => prev.map(agent => 
        agent.id === data.agentId 
          ? { ...agent, isOnline: data.isOnline, lastSeen: data.lastSeen }
          : agent
      ));
    };

    // Listen for bulk agent status updates
    const handleAgentsOnlineStatusUpdate = (data: { onlineAgents: Array<{ agentId: string; isOnline: boolean; lastSeen: string; agentName: string }> }) => {
      console.log('👨‍💼 TicketList: Bulk agent status update:', data);
      
      // Update agent presence map
      setAgentPresence(prev => {
        const updated = new Map(prev);
        
        // First mark all agents as offline
        prev.forEach((_, agentId) => {
          updated.set(agentId, false);
        });
        
        // Then mark online agents as online
        data.onlineAgents.forEach(agent => {
          updated.set(agent.agentId, agent.isOnline);
        });
        
        return updated;
      });
      
      // Update agents list
      setAgents(prev => prev.map(agent => {
        const onlineAgent = data.onlineAgents.find(oa => oa.agentId === agent.id);
        return onlineAgent 
          ? { ...agent, isOnline: onlineAgent.isOnline, lastSeen: onlineAgent.lastSeen }
          : { ...agent, isOnline: false };
      }));
    };

    // Import socket service dynamically to avoid circular dependencies
    import('../../services/socket').then(({ socketService }) => {
      socketService.on('agent_status_changed', handleAgentStatusChange);
      socketService.on('agents_online_status_update', handleAgentsOnlineStatusUpdate);
    });

    return () => {
      import('../../services/socket').then(({ socketService }) => {
        socketService.off('agent_status_changed', handleAgentStatusChange);
        socketService.off('agents_online_status_update', handleAgentsOnlineStatusUpdate);
      });
    };
  }, [user]);

  // Handle urgency changes to trigger row blinking
  const handleUrgencyChange = (ticketId: string, urgency: string, message: string) => {
    console.log(`Urgency changed for ticket ${ticketId}: ${urgency} - ${message}`);
    setBlinkingRows(prev => {
      const updated = new Map(prev);
      updated.set(ticketId, { urgency, startTime: Date.now() });
      return updated;
    });
  };

  // Handle customer message to trigger blue blinking
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCustomerMessage = (ticketId: string) => {
    setMessageBlinkingRows(prev => {
      const updated = new Set(prev);
      updated.add(ticketId);
      return updated;
    });
    onCustomerMessage?.(ticketId);
  };

  // Handle agent reply to stop blue blinking
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAgentReply = (ticketId: string) => {
    setMessageBlinkingRows(prev => {
      const updated = new Set(prev);
      updated.delete(ticketId);
      return updated;
    });
    onAgentReply?.(ticketId);
  };

  const filteredTickets = useMemo(() => {
    let filtered = tickets.filter(ticket => {
      // Search filter
      const searchFields = [
        ticket.title,
        ticket.description,
        ticket.customerName,
        ticket.customerCompany,
        ticket.customer?.name,
        ticket.ticketNumber
      ].filter(Boolean);
      
      const matchesSearch = !filters.search || filters.search.trim() === '' || searchFields.some(field => 
        field?.toString().toLowerCase().includes(filters.search.toLowerCase())
      );
      
      // Agent filter
      const matchesAgent = !filters.agent || filters.agent.includes('all') || 
        filters.agent.some(agentValue => {
          if (agentValue === 'unassigned') return !ticket.agentId;
          return ticket.agentId === agentValue;
        });
      
      // Customer Type filter
      const matchesCustomerType = !filters.customerType || filters.customerType.includes('all') || 
        filters.customerType.includes(ticket.customerType || 'Standard');
      
      // Category filter
      const ticketCategoryName = typeof ticket.category === 'string' 
        ? ticket.category 
        : (ticket.category as any)?.name || (ticket.category as any)?.categoryName;
      const matchesCategory = !filters.category || filters.category.includes('all') || 
        filters.category.includes(ticketCategoryName);
      
      // Date Range filter
      const matchesDateRange = !filters.dateRange || filters.dateRange.includes('all') || 
        filters.dateRange.some(range => {
          const createdDate = new Date(ticket.createdAt);
          const now = new Date();
          
          switch (range) {
            case 'today':
              return createdDate.toDateString() === now.toDateString();
            case 'yesterday':
              const yesterday = new Date(now);
              yesterday.setDate(yesterday.getDate() - 1);
              return createdDate.toDateString() === yesterday.toDateString();
            case 'this-week':
              const weekStart = new Date(now);
              weekStart.setDate(now.getDate() - now.getDay());
              return createdDate >= weekStart;
            case 'last-week':
              const lastWeekStart = new Date(now);
              lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
              const lastWeekEnd = new Date(lastWeekStart);
              lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
              return createdDate >= lastWeekStart && createdDate <= lastWeekEnd;
            case 'this-month':
              return createdDate.getMonth() === now.getMonth() && 
                     createdDate.getFullYear() === now.getFullYear();
            case 'last-month':
              const lastMonth = new Date(now);
              lastMonth.setMonth(now.getMonth() - 1);
              return createdDate.getMonth() === lastMonth.getMonth() && 
                     createdDate.getFullYear() === lastMonth.getFullYear();
            default:
              return true;
          }
        });
      
      // Priority filter
      const matchesPriority = !filters.priority || filters.priority.includes('all') || 
        filters.priority.includes(ticket.priority);
      
      // Status filter
      const matchesStatus = !filters.status || filters.status.includes('all') || 
        filters.status.some(statusValue => {
          if (statusValue === 'open') return isTicketOpen(ticket.status);
          if (statusValue === 'closed') return isTicketClosed(ticket.status);
          return ticket.status === statusValue;
        });
      
      return matchesSearch && matchesAgent && matchesCustomerType && 
             matchesCategory && matchesDateRange && matchesPriority && matchesStatus;
    });

    // Apply view-based filtering
    if (currentView === 'my-tickets' && user?.id) {
      filtered = filtered.filter(ticket => ticket.agentId === user.id);
    } else if (currentView === 'my-open-tickets' && user?.id) {
      filtered = filtered.filter(ticket => 
        ticket.agentId === user.id && isTicketOpen(ticket.status)
      );
    } else if (currentView === 'all-open-tickets') {
      filtered = filtered.filter(ticket => isTicketOpen(ticket.status));
    } else if (currentView === 'unassigned') {
      filtered = filtered.filter(ticket => !ticket.agentId);
    } else if (currentView === 'resolved') {
      filtered = filtered.filter(ticket => isTicketClosed(ticket.status));
    }

    return filtered;
  }, [tickets, filters, currentView, user?.id]);

  const getStatusColor = (status: string) => {
    return getStatusColorClasses(status as any);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 dark:text-red-400';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'low':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const now = currentTime; // Use state to trigger re-renders
    const messageTime = new Date(dateString).getTime();
    const diffMs = now - messageTime;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return new Date(messageTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getLastCustomerMessage = (ticket: Ticket) => {
    // Use database-backed timestamp if available
    if ((ticket as any).lastCustomerMessageAt) {
      return {
        id: 'db-timestamp',
        createdAt: (ticket as any).lastCustomerMessageAt,
        content: 'Customer message',
        sender: { userType: 'customer' }
      };
    }
    
    // Fallback to computing from messages array (for backwards compatibility)
    console.log(`🔍 Computing last customer message for ticket ${ticket.id} (fallback):`, {
      hasMessages: !!ticket.messages,
      messageCount: ticket.messages?.length || 0
    });
    
    if (!ticket.messages || ticket.messages.length === 0) return null;
    
    // Find the most recent message from a customer
    // Handle both registered customers and anonymous customers without accounts
    const customerMessages = ticket.messages
      .filter(msg => {
        const sender = msg.sender;
        const senderUserType = sender?.userType;
        
        // Check if it's a customer message using the same logic as CustomerChat
        // 1. Explicit customer userType
        if (senderUserType === 'customer') {
          return true;
        }
        
        // 2. Anonymous customer messages (no sender or no senderId/userId)
        if (!sender || (!msg.userId && !msg.senderId)) {
          return true;
        }
        
        // 3. For anonymous tickets, all non-agent/non-ai messages are customer messages
        if ((ticket as any).isAnonymous && senderUserType !== 'agent' && senderUserType !== 'ai') {
          return true;
        }
        
        // 4. If sender exists but no userType, and it's not an agent or AI, assume customer
        if (sender && !senderUserType && senderUserType !== 'agent' && senderUserType !== 'ai') {
          return true;
        }
        
        return false;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    return customerMessages.length > 0 ? customerMessages[0] : null;
  };

  const getLastAgentMessage = (ticket: Ticket) => {
    // Use database-backed timestamp if available
    if ((ticket as any).lastAgentMessageAt) {
      return {
        id: 'db-timestamp',
        createdAt: (ticket as any).lastAgentMessageAt,
        content: 'Agent message',
        sender: { userType: 'agent' }
      };
    }
    
    // Fallback to computing from messages array (for backwards compatibility)
    console.log(`🔍 Computing last agent message for ticket ${ticket.id} (fallback):`, {
      hasMessages: !!ticket.messages,
      messageCount: ticket.messages?.length || 0
    });
    
    if (!ticket.messages || ticket.messages.length === 0) return null;
    
    // Find the most recent message from an agent or AI
    const agentMessages = ticket.messages
      .filter(msg => msg.sender?.userType === 'agent' || msg.sender?.userType === 'ai')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    return agentMessages.length > 0 ? agentMessages[0] : null;
  };

  const formatStatus = (status: string) => {
    if (status === 'new') {
      return 'New - Not Assigned';
    }
    return getStatusDisplayName(status as any);
  };

  const getHeaderTitle = () => {
    switch (currentView) {
      case 'my-tickets':
        return 'My Tickets';
      case 'my-open-tickets':
        return 'My Open Tickets';
      case 'all-open-tickets':
        return 'All Open Tickets';
      case 'unassigned':
        return 'Unassigned Tickets';
      case 'resolved':
        return 'Resolved Tickets';
      default:
        return 'Support Tickets';
    }
  };

  const handleDeleteTicket = async (ticketId: string) => {
    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingTicketId(ticketId);
      console.log('Attempting to delete ticket:', ticketId);
      await apiService.deleteTicket(ticketId);
      console.log('Ticket deleted successfully');
      onRefresh(); // Refresh the ticket list
    } catch (error) {
      console.error('Error deleting ticket:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to delete ticket: ${errorMessage}`);
    } finally {
      setDeletingTicketId(null);
    }
  };

  const handleReassignTicket = async (ticketId: string, newAgentId: string) => {
    try {
      setReassigning(true);
      await apiService.reassignTicket(ticketId, newAgentId);
      setReassignTicketId(null);
      onRefresh(); // Refresh the ticket list
    } catch (error) {
      console.error('Error reassigning ticket:', error);
      alert('Failed to reassign ticket. Please try again.');
    } finally {
      setReassigning(false);
    }
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    
    const now = currentTime;
    const diffMs = now - lastUpdated.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    
    if (diffSeconds < 60) {
      return `Updated ${diffSeconds}s ago`;
    } else if (diffMinutes < 60) {
      return `Updated ${diffMinutes}m ago`;
    } else {
      return `Updated at ${lastUpdated.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
  };

  // Get row style for blinking effect
  const getRowStyle = (ticketId: string) => {
    const blinkData = blinkingRows.get(ticketId);
    const hasMessageBlink = messageBlinkingRows.has(ticketId);
    
    // Priority: message blinking (blue) takes precedence over urgency blinking
    if (hasMessageBlink) {
      return {
        backgroundColor: 'rgba(59, 130, 246, 0.2)', // blue with transparency
        animation: 'blink 0.5s ease-in-out infinite alternate',
        transition: 'background-color 0.3s ease'
      };
    }
    
    if (!blinkData) return {};

    const { urgency } = blinkData;
    let backgroundColor = '';
    
    switch (urgency) {
      case 'red':
        backgroundColor = 'rgba(239, 68, 68, 0.2)'; // red with transparency
        break;
      case 'yellow':
        backgroundColor = 'rgba(245, 158, 11, 0.2)'; // yellow with transparency
        break;
      case 'green':
        backgroundColor = 'rgba(34, 197, 94, 0.2)'; // green with transparency
        break;
      default:
        backgroundColor = 'rgba(156, 163, 175, 0.2)'; // gray with transparency
    }

    return {
      backgroundColor,
      animation: 'blink 0.5s ease-in-out infinite alternate',
      transition: 'background-color 0.3s ease'
    };
  };

  // Enhanced warning info function compatible with the new urgency system
  const getWarningInfo = (ticket: Ticket) => {
    const now = currentTime;
    const createdTime = new Date(ticket.createdAt).getTime();
    const elapsedMinutes = Math.floor((now - createdTime) / (1000 * 60));
    const isAssigned = !!ticket.agentId;
    const isResolved = isTicketClosed(ticket.status);
    const isInProgress = ticket.status === 'in_progress';
    
    // Check if AI is handling
    const isAIHandling = ticket.agentId === 'neuro-ai-agent' || 
      (ticket.messages && ticket.messages.some(msg => msg.sender?.userType === 'ai'));
    
    // Check if customer requested human
    const customerMessages = ticket.messages?.filter(msg => msg.sender?.userType === 'customer') || [];
    const customerRequestedHuman = customerMessages.some(msg => {
      const content = msg.content.toLowerCase();
      return content.includes('human') || content.includes('person') || 
             content.includes('agent') || content.includes('representative') ||
             content.includes('speak to someone') || content.includes('talk to someone');
    });

    if (isResolved) {
      return {
        level: 'success',
        message: '✅ Ticket resolved successfully',
        color: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700'
      };
    }

    // Assignment-based warnings
    if (!isAssigned || isAIHandling) {
      if (isAIHandling && !customerRequestedHuman) {
        return {
          level: 'info',
          message: '🤖 AI interaction ongoing',
          color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700'
        };
      }

      if (isAIHandling && customerRequestedHuman) {
        if (elapsedMinutes <= 3) {
          return {
            level: 'warning',
            message: '🟡 Human Agent requested, claim ticket now',
            color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700'
          };
        } else {
          return {
            level: 'critical',
            message: '🚨 Urgent! Human agent must take this ticket NOW',
            color: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700'
          };
        }
      }

      if (!isAIHandling && !isAssigned) {
        if (elapsedMinutes <= 1) {
          return {
            level: 'info',
            message: '🆕 Customer waiting for an agent',
            color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700'
          };
        } else if (elapsedMinutes <= 3) {  
          return {
            level: 'warning',
            message: '⚠️ Customer waiting >1 min, claim ticket ASAP',
            color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700'
          };
        } else {
          return {
            level: 'critical',
            message: 'Customer waiting too long, claim ticket NOW!',
            color: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700'
          };
        }
      }
    }

    // Status-based warnings (assigned to human agent)
    if (isAssigned && !isAIHandling) {
      if (isInProgress) {
        if (elapsedMinutes <= 5) {
          return {
            level: 'success',
            message: '✋ Support in progress (<5 min)',
            color: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700'
          };
        } else if (elapsedMinutes <= 10) {
          return {
            level: 'warning',
            message: '⏰ Support taking longer than expected (>5 min)',
            color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700'
          };
        } else {
          return {
            level: 'critical',
            message: 'Support taking too long - escalate now!',
            color: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700'
          };
        }
      } else {
        if (elapsedMinutes <= 5) {
          return {
            level: 'success',
            message: '✋ Assigned to agent - Customer being helped',
            color: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700'
          };
        } else {
          return {
            level: 'warning',
            message: '⏳ Assigned but not started - Agent should begin work',
            color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700'
          };
        }
      }
    }

    return {
      level: 'info',
      message: '📋 Normal priority',
      color: 'bg-gray-50 dark:bg-gray-900/20 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700'
    };
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-t-lg mb-2"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      {/* Add custom CSS for pulse and blink animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes blink {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:truncate">
            {getHeaderTitle()}
          </h2>
          <div className="flex items-center space-x-4 mt-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filteredTickets.length} of {tickets.length} tickets
            </p>
            {lastUpdated && (
              <p className="text-xs text-green-600 dark:text-green-400 flex items-center">
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatLastUpdated()}
              </p>
            )}
            {autoUpdating && (
              <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center">
                <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Auto-updating...
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          {/* Create Ticket button for agents with permission */}
          {user?.role === 'agent' && user?.permissions?.includes('tickets.create') && onCreateTicket && (
            <button
              onClick={onCreateTicket}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Ticket
            </button>
          )}
          <button
            onClick={onRefresh}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters - Using Board Toolbar */}
      <BoardToolbar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        totalTickets={tickets.length}
        filteredTickets={filteredTickets.length}
      />

      {/* Tickets Table */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No tickets found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {filters.search || 
             !filters.status.includes('all') || 
             !filters.priority.includes('all') ||
             !filters.agent.includes('all') ||
             !filters.customerType.includes('all') ||
             !filters.category.includes('all') ||
             !filters.dateRange.includes('all')
              ? 'Try adjusting your search or filter criteria.'
              : 'No tickets have been created yet.'}
          </p>
        </div>
      ) : (
        <ConfigurableTicketTable
          tickets={filteredTickets}
          onTicketSelect={onTicketSelect}
          onDeleteTicket={handleDeleteTicket}
          onReassignTicket={handleReassignTicket}
          urgencyIndicator={EnhancedUrgencyIndicator}
          getWarningInfo={getWarningInfo}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          formatDate={formatDate}
          formatRelativeTime={formatRelativeTime}
          getLastCustomerMessage={getLastCustomerMessage}
          getLastAgentMessage={getLastAgentMessage}
          formatStatus={formatStatus}
          getRowStyle={getRowStyle}
          handleUrgencyChange={handleUrgencyChange}
          agents={agents}
          agentPresence={agentPresence}
          reassignTicketId={reassignTicketId}
          setReassignTicketId={setReassignTicketId}
          reassigning={reassigning}
          deletingTicketId={deletingTicketId}
          exportFilters={{
            searchTerm: filters.search,
            statusFilter: filters.status.includes('all') ? 'all' : filters.status[0],
            priorityFilter: filters.priority.includes('all') ? 'all' : filters.priority[0],
            currentView
          }}
        />
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => onPageChange && onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                currentPage === 1
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => onPageChange && onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                currentPage === totalPages
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center space-x-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </p>
              <div className="flex items-center space-x-2">
                <label htmlFor="items-per-page" className="text-sm text-gray-700 dark:text-gray-300">
                  Items per page:
                </label>
                <select
                  id="items-per-page"
                  value={itemsPerPage}
                  onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(Number(e.target.value))}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => onPageChange && onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium ${
                    currentPage === 1
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNumber: number;
                  if (totalPages <= 7) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 4) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNumber = totalPages - 6 + i;
                  } else {
                    pageNumber = currentPage - 3 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => onPageChange && onPageChange(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNumber
                          ? 'z-10 bg-primary-50 dark:bg-primary-900 border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => onPageChange && onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium ${
                    currentPage === totalPages
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketList; 